/**
 * Proteção dos campos numéricos (#288)
 *
 * O `<input type="number">` do navegador aceita, além dos dígitos, "e"/"E" (notação
 * científica: 1e12), "+", "-" e o separador decimal. Com um valor inválido (ex.: "e12"),
 * o navegador mostra o texto, mas entrega o campo vazio ao sistema, e o usuário acha que
 * salvou um número. Esta proteção, instalada uma vez para todo o app (painel, loja
 * online, mesa), recusa na digitação e no colar:
 * - "e", "E" e "+", sempre;
 * - "-", quando o campo não aceita negativo (`min` >= 0);
 * - "." e ",", quando o campo é de número inteiro (`step` ausente ou inteiro; o padrão do
 *   navegador é 1). Campos com `step="0.01"` ou `step="any"` aceitam decimal.
 *
 * Nos campos decimais, a vírgula vira ponto: o navegador descarta a vírgula do
 * `type="number"` (com qualquer idioma), e "12,50" virava 1250. Um segundo separador é
 * recusado. No colar, valores no formato brasileiro ("R$ 1.234,56") viram "1234.56".
 *
 * @author Equipe VendeAi
 * @since 1.6.0
 */

interface NumericRules {
  allowNegative: boolean;
  allowDecimal: boolean;
}

/** Regras do campo, pelos atributos `min` e `step` */
export function numericRules(input: Pick<HTMLInputElement, 'getAttribute'>): NumericRules {
  const min = input.getAttribute('min');
  const minValue = min === null || min.trim() === '' ? NaN : Number(min);
  const step = (input.getAttribute('step') || '').trim().toLowerCase();
  const stepValue = step === '' ? 1 : Number(step);
  return {
    allowNegative: !(Number.isFinite(minValue) && minValue >= 0),
    allowDecimal: step === 'any' || (Number.isFinite(stepValue) && !Number.isInteger(stepValue)),
  };
}

/** O caractere pode entrar no campo? */
export function isAllowedNumericChar(char: string, rules: NumericRules): boolean {
  if (/^[0-9]$/.test(char)) return true;
  if (char === '-') return rules.allowNegative;
  if (char === '.' || char === ',') return rules.allowDecimal;
  return false;
}

/** O texto colado é um número válido para o campo? (espaços nas pontas são ignorados) */
export function isAllowedNumericText(text: string, rules: NumericRules): boolean {
  const value = text.trim();
  if (value === '') return true;
  const sign = rules.allowNegative ? '-?' : '';
  const decimal = rules.allowDecimal ? '([.,][0-9]*)?' : '';
  return new RegExp(`^${sign}[0-9]*${decimal}$`).test(value) && /[0-9]/.test(value);
}

/**
 * Texto colado convertido para o formato do campo: tira "R$" e espaços; no decimal, o
 * formato brasileiro ("1.234,56") vira "1234.56"; no inteiro, aceita milhar com ponto
 * ("1.234"). null = não é um número válido para o campo.
 */
export function normalizePastedNumber(text: string, rules: NumericRules): string | null {
  let value = text.replace(/R\$/gi, '').replace(/\s+/g, '');
  if (value === '') return '';
  if (rules.allowDecimal) {
    if (value.includes(',')) value = value.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(value)) {
    value = value.replace(/\./g, '');
  }
  return isAllowedNumericText(value, rules) ? value : null;
}

/** Insere o texto na posição do cursor, como se fosse digitado (o React recebe o input) */
function insertText(text: string): boolean {
  try {
    return document.execCommand('insertText', false, text);
  } catch {
    return false;
  }
}

/** O navegador sabe inserir texto pelo execCommand (sem isso, o separador segue o padrão) */
function canInsertText(): boolean {
  try {
    return document.queryCommandSupported?.('insertText') ?? false;
  } catch {
    return false;
  }
}

/** Há texto selecionado no campo (digitar substitui a seleção) */
function hasSelection(): boolean {
  return (document.getSelection?.()?.toString() ?? '') !== '';
}

/**
 * Separador decimal digitado: a vírgula entra como ponto, um só (com o valor selecionado,
 * substitui a seleção). O ponto fica com o navegador. Sem suporte ao execCommand, a
 * vírgula é recusada (o ponto continua funcionando).
 */
function handleSeparator(event: Event, input: HTMLInputElement, char: string): void {
  // O ponto segue o navegador, que já recusa um segundo ponto e substitui a seleção
  if (char === '.') return;
  event.preventDefault();
  if (!canInsertText()) return;
  if (hasSelection() || !input.value.includes('.')) insertText('.');
}

function numberInput(target: EventTarget | null): HTMLInputElement | null {
  return target instanceof HTMLInputElement && target.type === 'number' && !target.readOnly && !target.disabled
    ? target
    : null;
}

let installed = false;

/** Instala a proteção em todo o documento (uma vez) */
export function installNumericInputGuard(doc: Document = document): void {
  if (installed) return;
  installed = true;

  // Teclado físico: a tecla recusada nem chega ao campo
  doc.addEventListener(
    'keydown',
    event => {
      const input = numberInput(event.target);
      if (!input || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length !== 1) return; // Backspace, setas, Tab etc.
      const rules = numericRules(input);
      if (!isAllowedNumericChar(event.key, rules)) {
        event.preventDefault();
        return;
      }
      // Um separador só; a vírgula entra como ponto (o navegador descartaria a vírgula)
      if (event.key === '.' || event.key === ',') handleSeparator(event, input, event.key);
    },
    true,
  );

  // Teclado do celular (algumas teclas chegam como "Unidentified" no keydown)
  doc.addEventListener(
    'beforeinput',
    event => {
      const input = numberInput(event.target);
      const data = (event as InputEvent).data;
      if (!input || !data) return;
      const rules = numericRules(input);
      if ([...data].some(char => !isAllowedNumericChar(char, rules))) {
        event.preventDefault();
        return;
      }
      // Teclado do celular: a vírgula (ou o ponto) vira um ponto só
      if (data === ',' || data === '.') handleSeparator(event, input, data);
    },
    true,
  );

  // Colar: só entra um número válido para o campo
  doc.addEventListener(
    'paste',
    event => {
      const input = numberInput(event.target);
      if (!input) return;
      const text = event.clipboardData?.getData('text') ?? '';
      const normalized = normalizePastedNumber(text, numericRules(input));
      if (normalized === null) {
        event.preventDefault();
      } else if (normalized !== text && canInsertText()) {
        event.preventDefault();
        insertText(normalized);
      }
    },
    true,
  );

  // Arrastar e soltar texto no campo
  doc.addEventListener(
    'drop',
    event => {
      const input = numberInput(event.target);
      if (!input) return;
      const text = event.dataTransfer?.getData('text') ?? '';
      if (!isAllowedNumericText(text, numericRules(input))) event.preventDefault();
    },
    true,
  );
}
